import React, { useEffect, useState } from "react";
import styled from "styled-components";

export const APP_STORE_URL = "https://apps.apple.com/be/app/nea/id6753867622";
// The Android app is not published yet: Android visitors are sent to the "notify me" form instead
export const ANDROID_NOTIFY_ANCHOR = "#android";

// "desktop" is also the pre-detection default, so both buttons render and nothing jumps
const usePlatform = () => {
  const [platform, setPlatform] = useState("desktop");

  useEffect(() => {
    const hinted = navigator.userAgentData?.platform || "";
    const ua = navigator.userAgent || "";
    if (hinted === "iOS" || /iPhone|iPad|iPod/i.test(ua)) setPlatform("ios");
    else if (hinted === "Android" || /Android/i.test(ua)) setPlatform("android");
  }, []);

  return platform;
};

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: ${(props) => (props.$center ? "center" : "flex-start")};

  @media screen and (max-width: 48em) {
    justify-content: center;
    width: 100%;
  }
`;

const Button = styled.a`
  height: 52px;
  padding: 0 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  border-radius: var(--radius-full);
  font-family: var(--fontR);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: background 150ms ease, transform 150ms ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--green);
    outline-offset: 3px;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media screen and (max-width: 30em) {
    width: 100%;
    padding: 0 1rem;
    font-size: 0.9375rem;
  }
`;

const Primary = styled(Button)`
  background: ${(props) => (props.$tone === "dark" ? "#fafaf8" : "#0a0a0b")};
  color: ${(props) => (props.$tone === "dark" ? "#0a0a0b" : "#fafaf8")};

  &:hover {
    background: ${(props) => (props.$tone === "dark" ? "#ffffff" : "#232327")};
  }
`;

const Outline = styled(Button)`
  background: transparent;
  color: ${(props) => (props.$tone === "dark" ? "#fafaf8" : "#0a0a0b")};
  border: 1px solid ${(props) => (props.$tone === "dark" ? "#3a3a40" : "#c9c9c1")};

  &:hover {
    background: ${(props) => (props.$tone === "dark" ? "#141416" : "rgba(10, 10, 11, 0.04)")};
  }
`;

// Smaller outlined button used under each feature, worded as the benefit
const FeatureLink = styled(Outline)`
  height: 46px;
  padding: 0 1.375rem;
  font-size: 0.9375rem;

  @media screen and (max-width: 30em) {
    width: auto;
  }
`;

const AppleGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const storeProps = (platform, position) =>
  platform === "android"
    ? { href: ANDROID_NOTIFY_ANCHOR, "data-store": "android-notify", "data-position": position }
    : {
        href: APP_STORE_URL,
        target: "_blank",
        rel: "noopener noreferrer",
        "data-store": "app-store",
        "data-position": position,
      };

/**
 * Download buttons. `tone` is the background they sit on ("dark" or "light"),
 * `position` identifies the block for analytics (hero, tarif, ...).
 */
const StoreButtons = ({ tone = "light", position, center = false }) => {
  const platform = usePlatform();

  return (
    <Row $center={center}>
      {platform !== "android" && (
        <Primary $tone={tone} {...storeProps("ios", position)}>
          <AppleGlyph />
          Télécharger sur l'App Store
        </Primary>
      )}
      {platform !== "ios" && (
        <Outline $tone={tone} {...storeProps("android", position)}>
          Android&nbsp;: me prévenir
        </Outline>
      )}
    </Row>
  );
};

/** Single benefit-worded link: App Store everywhere, the notify form on Android. */
export const DownloadLink = ({ tone = "light", position, children }) => {
  const platform = usePlatform();

  return (
    <FeatureLink $tone={tone} {...storeProps(platform, position)}>
      {children}
    </FeatureLink>
  );
};

export default StoreButtons;
